import React, { useState, useEffect, useRef } from "react";
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import zureSmileLogo from "../../assets/images/zuresmile.webp";
import menuItems from "./menuData";
import { useNavigate, useLocation } from "react-router-dom";
import API from "../../ApiService/ApiService";

const api = new API();

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  const [clinicsPinCache, setClinicsPinCache] = useState([]);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);

  // Hide header when scrolling down past 300px; re-appear smoothly when scrolling up
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 300 && currentScrollY > lastScrollY) {
        // Scrolling down past threshold -> hide
        setIsHeaderHidden(true);
        setShowCategoryDropdown(false);
        setShowSuggestions(false);
      } else {
        // Scrolling up or near top -> show
        setIsHeaderHidden(false);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Clean Professional Stroke Icons (No Emojis)
  const StrokeIcons = {
    all: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    clinic: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0f766e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 9h6M12 6v6" />
      </svg>
    ),
    doctor: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 3h15M12 3v10a4 4 0 0 1-8 0V3M12 17a4 4 0 0 0 8 0V3" />
        <circle cx="18" cy="19" r="2" />
        <path d="M16 19h-2a2 2 0 0 1-2-2" />
      </svg>
    ),
    province: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    ),
    district: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    secretariat: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="21" x2="21" y2="21" />
        <line x1="6" y1="21" x2="6" y2="9" />
        <line x1="18" y1="21" x2="18" y2="9" />
        <line x1="12" y1="21" x2="12" y2="9" />
        <polygon points="12 2 20 7 4 7" />
      </svg>
    ),
    pincode: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e11d48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
  };

  const CATEGORIES = [
    { key: "all", label: "All Categories", icon: StrokeIcons.all, shortLabel: "All", placeholder: "Search City / Clinic" },
    { key: "clinic", label: "Clinics", icon: StrokeIcons.clinic, shortLabel: "Clinics", placeholder: "Search clinic name..." },
    { key: "doctor", label: "Doctors", icon: StrokeIcons.doctor, shortLabel: "Doctors", placeholder: "Search doctor or specialist..." },
    { key: "pincode", label: "Postal / Pincode", icon: StrokeIcons.pincode, shortLabel: "Pincode", placeholder: "Search postal code (e.g. 00400)..." },
    { key: "province", label: "Provinces", icon: StrokeIcons.province, shortLabel: "Provinces", placeholder: "Search province (e.g. Western)..." },
    { key: "district", label: "Districts", icon: StrokeIcons.district, shortLabel: "Districts", placeholder: "Search district (e.g. Colombo)..." },
    { key: "secretariat", label: "Secretariats", icon: StrokeIcons.secretariat, shortLabel: "Secretariats", placeholder: "Search D.S. division..." },
  ];

  const currentCategory = CATEGORIES.find((c) => c.key === selectedCategory) || CATEGORIES[0];

  const desktopSearchBoxRef = useRef(null);
  const mobileSearchBoxRef = useRef(null);
  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };

  const handleScroll = () => {
    if (window.scrollY > 50) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close suggestions and category dropdown when route changes
  useEffect(() => {
    setShowSuggestions(false);
    setShowCategoryDropdown(false);
    setSuggestions([]);
    setIsInputFocused(false);
  }, [location.pathname, location.search]);

  // Close suggestions & category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideDesktop =
        desktopSearchBoxRef.current &&
        desktopSearchBoxRef.current.contains(event.target);
      const isInsideMobile =
        mobileSearchBoxRef.current &&
        mobileSearchBoxRef.current.contains(event.target);
      if (!isInsideDesktop && !isInsideMobile) {
        setShowSuggestions(false);
        setShowCategoryDropdown(false);
        setIsInputFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Sri Lanka standard reference data for instant accurate province/district matching
  const SRI_LANKA_PROVINCES = [
    "Western Province",
    "Central Province",
    "Southern Province",
    "Northern Province",
    "Eastern Province",
    "North Western Province",
    "North Central Province",
    "Uva Province",
    "Sabaragamuwa Province",
  ];

  const SRI_LANKA_DISTRICTS = [
    "Colombo",
    "Gampaha",
    "Kalutara",
    "Kandy",
    "Matale",
    "Nuwara Eliya",
    "Galle",
    "Matara",
    "Hambantota",
    "Jaffna",
    "Kilinochchi",
    "Mannar",
    "Vavuniya",
    "Mullaitivu",
    "Batticaloa",
    "Ampara",
    "Trincomalee",
    "Kurunegala",
    "Puttalam",
    "Anuradhapura",
    "Polonnaruwa",
    "Badulla",
    "Monaragala",
    "Ratnapura",
    "Kegalle",
  ];

  // Live Auto-Complete Suggestions Fetch with Strict Category Filter
  useEffect(() => {
    if (!isInputFocused) {
      setShowSuggestions(false);
      return;
    }

    const trimmed = search.trim();
    if (trimmed.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(() => {
      // 1. Local instant fallback for provinces
      if (selectedCategory === "province") {
        const matched = SRI_LANKA_PROVINCES.filter((p) =>
          p.toLowerCase().includes(trimmed.toLowerCase())
        ).map((p) => ({
          type: "province",
          label: p,
          subtext: "Province / Region",
          value: p.replace(" Province", ""),
        }));
        setSuggestions(matched);
        setShowSuggestions(matched.length > 0);
        return;
      }

      // 2. Local instant fallback for districts
      if (selectedCategory === "district") {
        const matched = SRI_LANKA_DISTRICTS.filter((d) =>
          d.toLowerCase().includes(trimmed.toLowerCase())
        ).map((d) => ({
          type: "district",
          label: `${d} District`,
          subtext: "District / Area",
          value: d,
        }));
        setSuggestions(matched);
        setShowSuggestions(matched.length > 0);
        return;
      }

      // 3. Instant Postal Code / Pincode matching
      if (selectedCategory === "pincode" || (selectedCategory === "all" && /^\d+$/.test(trimmed))) {
        const filterPins = (pinsList) => {
          const matchedPins = [];
          const seen = new Set();
          pinsList.forEach((c) => {
            const pinStr = String(c.pin_code || "");
            const padPin = pinStr.length <= 4 ? pinStr.padStart(5, "0") : pinStr;
            if (
              pinStr.includes(trimmed) ||
              padPin.includes(trimmed) ||
              (String(Number(trimmed)) === pinStr)
            ) {
              if (c.pin_code && !seen.has(c.pin_code)) {
                seen.add(c.pin_code);
                matchedPins.push({
                  type: "pincode",
                  label: `Postal Code ${padPin}`,
                  subtext: `${c.name} • ${c.address_line1 || "Area"}`,
                  value: String(c.pin_code),
                });
              }
            }
          });
          setSuggestions(matchedPins);
          setShowSuggestions(matchedPins.length > 0);
        };

        if (clinicsPinCache.length > 0) {
          filterPins(clinicsPinCache);
        } else {
          api.getAll("clinic/pinPoint", {}, (err, res) => {
            if (!err && res?.data?.status === 200 && isInputFocused) {
              const data = res?.data?.data || [];
              setClinicsPinCache(data);
              filterPins(data);
            }
          });
        }
        return; // CRITICAL: Stop here so clinic/suggestions is not called in parallel!
      }

      // 4. Remote API for Clinics, Doctors, Secretariats
      api.getAll(
        "clinic/suggestions",
        { query: trimmed, category: selectedCategory },
        (err, res) => {
          if (!err && res?.data?.status === 200 && isInputFocused) {
            let results = res?.data?.data || [];

            // Strict client-side category filtering
            if (selectedCategory === "clinic") {
              results = results.filter((i) => i.type === "clinic");
            } else if (selectedCategory === "doctor") {
              results = results.filter((i) => i.type === "doctor");
            } else if (selectedCategory === "secretariat") {
              results = results.filter(
                (i) =>
                  i.type === "secretariat" ||
                  (i.subtext && i.subtext.toLowerCase().includes("division")) ||
                  (i.subtext && i.subtext.toLowerCase().includes("area"))
              );
            }

            setSuggestions(results);
            setShowSuggestions(results.length > 0);
          }
        }
      );
    }, 180);

    return () => clearTimeout(timer);
  }, [search, isInputFocused, selectedCategory, clinicsPinCache]);

  // Sync search state with URL search parameter if present
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlQuery = urlParams.get("search");
      const urlCat = urlParams.get("category");
      if (urlQuery) {
        setSearch(urlQuery);
      }
      if (urlCat && CATEGORIES.some((c) => c.key === urlCat)) {
        setSelectedCategory(urlCat);
      }
    } catch (e) { }
  }, [location.search]);

  const handleSearchSubmit = (searchTerm = search) => {
    setShowSuggestions(false);
    setShowCategoryDropdown(false);
    setSuggestions([]);
    setIsInputFocused(false);
    desktopInputRef.current?.blur();
    mobileInputRef.current?.blur();

    const query = searchTerm ? searchTerm.trim() : "";
    const catQuery = selectedCategory !== "all" ? `&category=${encodeURIComponent(selectedCategory)}` : "";
    if (query !== "") {
      navigate(`/search?search=${encodeURIComponent(query)}${catQuery}`);
    } else {
      navigate(`/search${catQuery ? `?${catQuery.substring(1)}` : ""}`);
    }
  };

  // If user chooses doctor -> profile; if clinic/pincode/province/district -> search page!
  const handleSelectSuggestion = (item) => {
    setShowSuggestions(false);
    setShowCategoryDropdown(false);
    setSuggestions([]);
    setIsInputFocused(false);
    desktopInputRef.current?.blur();
    mobileInputRef.current?.blur();

    const selectedValue = item.value || item.label || "";
    setSearch(selectedValue);

    if (item.type === "doctor" && item.slug) {
      // Redirect to the respective doctor's profile page
      navigate(`/doctor/${item.slug}`);
    } else {
      // For clinics, pincodes, provinces, districts & secretariats -> show in search page!
      navigate(`/search?search=${encodeURIComponent(selectedValue)}${selectedCategory !== "all" ? `&category=${encodeURIComponent(selectedCategory)}` : ""}`);
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setIsInputFocused(false);
    desktopInputRef.current?.blur();
    mobileInputRef.current?.blur();
    navigate("/search");
  };

  return (
    <header
      className={`site-header transition-all duration-300 ${
        isHeaderHidden ? "header-hidden" : ""
      }`}
    >
      <div className="container mx-auto px-4 max-w-[1320px]">
        {/* Desktop Header */}
        <div className="hidden lg:block">
          <div className="site-header-bottom">
            <div className="site-logo-div flex-shrink-0">
              <a href="https://www.zuresmile.com/" title="Zure Smile Aligners">
                <img
                  src="https://www.zuresmile.com/app/static/media/zuresmile.e5a2fd425826e3ce6e2c.webp"
                  alt="ZureSmile Aligners Logo"
                  width="180"
                  height="45"
                  decoding="async"
                />
              </a>
            </div>

            <div className="site-search-div" ref={desktopSearchBoxRef}>
              <a
                href="https://zuresmile.com/app/search?search="
                className="doc-txt"
              >
                Find a Clinic
              </a>
              <div className="site-search-div-divider"></div>

              {/* Sleek Compact Category-Enhanced Search Box */}
              <form
                className="site-search-container"
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "#ffffff",
                  border: "1px solid #268F81",
                  borderRadius: "9999px",
                  padding: "4px 6px 4px 6px",
                  gap: "6px",
                  position: "relative",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                  transition: "box-shadow 0.2s ease"
                }}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearchSubmit();
                }}
              >
                {/* Category Dropdown Selector */}
                <div className="category-dropdown-wrapper relative" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryDropdown(!showCategoryDropdown);
                      setShowSuggestions(false);
                    }}
                    className="category-btn"
                    title="Search by category"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "4px 9px",
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#334155",
                      background: "#f8fafc",
                      border: "none",
                      borderRadius: "9999px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      userSelect: "none"
                    }}
                  >
                    <span>{currentCategory.icon}</span>
                    <span>{currentCategory.shortLabel}</span>
                    <span style={{ fontSize: "9px", color: "#94a3b8" }}>▾</span>
                  </button>

                  {/* Dropdown Menu */}
                  {showCategoryDropdown && (
                    <div className="category-menu absolute left-0 top-[130%] w-[210px] bg-white rounded-xl shadow-2xl border border-slate-200 py-1.5 z-[100] animate-fadeIn">
                      <div className="category-menu-title px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                        Search By Category
                      </div>
                      {CATEGORIES.map((cat) => (
                        <div
                          key={cat.key}
                          onClick={() => {
                            setSelectedCategory(cat.key);
                            setShowCategoryDropdown(false);
                            if (search.trim().length >= 1) {
                              setIsInputFocused(true);
                            }
                          }}
                          className={`category-menu-item px-3 py-2 text-xs flex items-center justify-between cursor-pointer transition-colors ${selectedCategory === cat.key
                              ? "bg-teal-50 text-teal-800 font-bold"
                              : "text-slate-700 hover:bg-slate-50 font-medium"
                            }`}
                        >
                          <span className="flex items-center gap-2">
                            <span>{cat.icon}</span>
                            <span>{cat.label}</span>
                          </span>
                          {selectedCategory === cat.key && (
                            <span className="text-teal-600 font-bold text-xs">✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ width: "1px", height: "16px", background: "#e2e8f0", margin: "0 2px" }}></div>

                <input
                  ref={desktopInputRef}
                  className="site-search-input"
                  type="text"
                  placeholder={currentCategory.placeholder}
                  value={search}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "13px",
                    width: "165px",
                    color: "#1e293b",
                    padding: "4px 2px",
                    margin: 0,
                    boxShadow: "none"
                  }}
                  onFocus={() => {
                    setIsInputFocused(true);
                    setShowCategoryDropdown(false);
                    if (suggestions.length > 0 && search.trim().length >= 1) {
                      setShowSuggestions(true);
                    }
                  }}
                  onChange={(e) => {
                    setIsInputFocused(true);
                    setSearch(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                    if (e.key === "Escape") {
                      setShowSuggestions(false);
                      setIsInputFocused(false);
                    }
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="text-gray-400 hover:text-gray-600 px-1 flex items-center justify-center cursor-pointer border-0 bg-transparent"
                    title="Clear Search"
                  >
                    <CloseCircleFilled style={{ fontSize: "13px" }} />
                  </button>
                )}
                <button
                  type="submit"
                  className="site-go-button"
                  style={{
                    backgroundColor: "#268F81",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "9999px",
                    padding: "5px 15px",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                    transition: "background-color 0.2s ease"
                  }}
                >
                  Go
                </button>

                {/* Floating Live Suggestions Dropdown */}
                {showSuggestions && isInputFocused && suggestions.length > 0 && (
                  <div className="absolute left-0 top-[110%] w-full bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 max-h-[380px] overflow-y-auto font-secondary">
                    <div className="px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 flex justify-between items-center">
                      <span>Matching {currentCategory.shortLabel}</span>
                      <span>{suggestions.length} Results</span>
                    </div>
                    {suggestions.map((item, idx) => {
                      const itemIcon =
                        StrokeIcons[item.type] || StrokeIcons.clinic;
                      return (
                        <div
                          key={idx}
                          className="px-4 py-2.5 hover:bg-teal-50 cursor-pointer transition-colors flex items-center gap-3 border-b border-slate-50 last:border-none"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectSuggestion(item);
                          }}
                        >
                          <span className="p-1.5 rounded-md bg-slate-50 border border-slate-100 flex-shrink-0">
                            {itemIcon}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-slate-800 truncate">
                              {item.label}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{item.subtext}</div>
                          </div>
                          <span className="text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-teal-50 text-teal-700 border border-teal-200/60">
                            {item.type}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </form>
            </div>

            <div id="cssmenu">
              <ul className="site-menu">
                {menuItems.map((item) => (
                  <li key={item.key}>
                    <a
                      href={item.path}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="block lg:hidden py-2">
          <div className="flex justify-between items-center">
            <a
              href="https://www.zuresmile.com/"
              className="flex-shrink-0 site-logo-div"
              title="Visit ZureSmile Official Website"
            >
              <img
                src="https://www.zuresmile.com/app/static/media/zuresmile.e5a2fd425826e3ce6e2c.webp"
                alt="ZureSmile"
                width="130"
                height="35"
                decoding="async"
                className="w-[130px] h-auto object-contain"
              />
            </a>
            <div>
              <div
                className="bg-[#268F81] w-[40px] h-[40px] rounded-lg flex justify-center items-center cursor-pointer shadow"
                onClick={() => showDrawer()}
              >
                {open ? (
                  <MenuUnfoldOutlined className="text-white text-lg" />
                ) : (
                  <MenuFoldOutlined className="text-white text-lg" />
                )}
              </div>
            </div>
          </div>

          {/* Mobile Category & Search Bar */}
          <div className="mt-3 relative" ref={mobileSearchBoxRef}>
            {/* Category Pills Bar */}
            <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none mb-1.5">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`px-2.5 py-1 text-xs rounded-full font-medium whitespace-nowrap transition-all border cursor-pointer flex items-center gap-1.5 ${selectedCategory === cat.key
                      ? "bg-[#268F81] text-white border-[#268F81] shadow-xs"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.shortLabel}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                ref={mobileInputRef}
                className="search-input w-full text-sm"
                type="text"
                placeholder={currentCategory.placeholder}
                value={search}
                onFocus={() => {
                  setIsInputFocused(true);
                  if (suggestions.length > 0 && search.trim().length >= 1) {
                    setShowSuggestions(true);
                  }
                }}
                onChange={(e) => {
                  setIsInputFocused(true);
                  setSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchSubmit();
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="text-gray-400 hover:text-gray-600 p-1 border-0 bg-transparent cursor-pointer"
                >
                  <CloseCircleFilled style={{ fontSize: "16px" }} />
                </button>
              )}
              <button
                className="go-button text-sm px-4 py-2 cursor-pointer border-0"
                onClick={() => handleSearchSubmit()}
              >
                Go
              </button>
            </div>

            {/* Mobile Live Suggestions Dropdown */}
            {showSuggestions && isInputFocused && suggestions.length > 0 && (
              <div className="absolute left-0 top-[110%] w-full bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 max-h-[300px] overflow-y-auto font-secondary">
                {suggestions.map((item, idx) => {
                  const itemIcon =
                    StrokeIcons[item.type] || StrokeIcons.clinic;
                  return (
                    <div
                      key={idx}
                      className="px-4 py-2 hover:bg-teal-50 cursor-pointer transition-colors flex items-center gap-3 border-b border-slate-50 last:border-none"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectSuggestion(item);
                      }}
                    >
                      <span className="p-1 rounded bg-slate-50 border border-slate-100 flex-shrink-0">
                        {itemIcon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-xs text-slate-800 truncate">
                          {item.label}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{item.subtext}</div>
                      </div>
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-teal-50 text-teal-700">
                        {item.type}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Native Mobile Slide-Over Drawer (0KB overhead) */}
      <div
        className={`fixed inset-0 z-[99999] transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
      >
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-xs"
          onClick={onClose}
        />
        <div
          className={`absolute top-0 right-0 bottom-0 w-[280px] bg-white shadow-2xl p-6 flex flex-col gap-6 transform transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <a
              href="https://www.zuresmile.com/"
              onClick={onClose}
              title="Visit ZureSmile Official Website"
            >
              <img src={zureSmileLogo} alt="ZureSmile" className="w-[120px] h-auto object-contain" />
            </a>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg border-0 bg-transparent cursor-pointer text-lg font-bold"
            >
              ✕
            </button>
          </div>
          <div className="flex flex-col gap-3 overflow-y-auto">
            {menuItems.map((item) => (
              <a href={item.path} key={item.key} onClick={onClose}>
                <div className="font-secondary font-semibold text-base text-[#334155] hover:text-primary transition py-2 border-b border-gray-50">
                  {item.label}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
